/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useMemo, useState } from 'react';
import { Pagination, Tag, Typography } from 'antd';
import { PaperClipOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import moment from 'moment';
import IconButton from '~components/_common/IconButton/IconButton';
import { useQuery } from 'react-query';
import {
  getAllMessages,
  getGlobalEvaluationInstantMessageAttachmentLocation,
} from '~src/services/api/movements';

const HISTORY_PAGE_SIZE = 2;

type HistoryInstantMessage = {
  message_id: number;
  id?: number;
  title: string;
  description: string;
  type: 2 | 5 | 6;
  reference?: string | null;
  sent_at?: number;
  created_by_full_name: string;
  created_at: number;
  attachments?: {
    file_guid: string;
    name: string;
  }[];
};

export type HistoryTabProps = {
  projectId: number;
  tenderId: number;
  visible: boolean;
  readOnly: boolean;
};

export const HistoryTab: React.FC<HistoryTabProps> = ({
  projectId,
  tenderId,
  visible,
  readOnly,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const [historyPage, setHistoryPage] = useState(1);

  // Query history messages
  const { data: historyMessages = [], isLoading } = (useQuery(
    ['all_tender_messages', projectId, tenderId],
    () =>
      getAllMessages({
        projectId: Number(projectId),
        tenderId: Number(tenderId),
        type: [6],
        sort: 'desc(sent_at)',
      }),
    { enabled: !!visible && !readOnly && !!projectId && !!tenderId }
  ) as any) as { data: HistoryInstantMessage[]; isLoading: boolean };

  // Paginate history messages
  const paginatedHistory = useMemo(() => {
    const start = (historyPage - 1) * HISTORY_PAGE_SIZE;
    return historyMessages.slice(start, start + HISTORY_PAGE_SIZE);
  }, [historyMessages, historyPage]);

  // Handle attachment download
  const handleDownloadAttachment = async (
    instantMessageId: number,
    fileGuid: string
  ) => {
    const url = await getGlobalEvaluationInstantMessageAttachmentLocation({
      projectId,
      tenderId,
      instantMessageId,
      fileGuid,
    });
    if (url) window.open(url, '_blank');
  };

  if (isLoading) {
    return (
      <div css={styles.wrapper}>
        <div
          css={{
            padding: 16,
            border: '1px solid #f0f0f0',
            borderRadius: 6,
            background: '#fafafa',
          }}>
          <Typography.Text type="secondary">
            {intl.formatMessage({ id: 'loading' })}
          </Typography.Text>
        </div>
      </div>
    );
  }

  if (!historyMessages?.length) {
    return (
      <div css={styles.wrapper}>
        <div
          css={{
            padding: 16,
            border: '1px solid #f0f0f0',
            borderRadius: 6,
            background: '#fafafa',
          }}>
          <Typography.Text type="secondary">
            {intl.formatMessage({ id: 'no-data' })}
          </Typography.Text>
        </div>
      </div>
    );
  }

  return (
    <div css={styles.wrapper}>
      <div>
        <div css={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {paginatedHistory.map((msg: HistoryInstantMessage) => {
            const currentId = msg.id || msg.message_id;

            return (
              <div
                key={msg.message_id || msg.id}
                css={{
                  border: '1px solid #f0f0f0',
                  borderRadius: 8,
                  background: '#fff',
                  padding: '16px 16px 10px 16px',
                }}>
                <div css={{ display: 'block' }}>
                  {/* RIGHT BLOCK: TAG */}
                  <div
                    css={{
                      float: 'right',
                      marginLeft: '24px',
                      marginBottom: '8px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 12,
                    }}>
                    <div
                      css={{
                        height: 32,
                        display: 'flex',
                        alignItems: 'center',
                      }}>
                      <Tag color="purple" style={{ margin: 0 }}>
                        {intl.formatMessage({
                          id: 'evaluation-instant-message',
                        })}
                      </Tag>
                    </div>
                  </div>

                  {/* LEFT: Title & Description */}
                  <Typography.Title
                    level={5}
                    css={{
                      marginTop: '5px',
                      marginBottom: 0,
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                    }}>
                    {msg.title || '-'}
                  </Typography.Title>

                  {!!msg.description && (
                    <Typography.Paragraph
                      css={{
                        marginTop: 8,
                        marginBottom: 0,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                      }}>
                      {msg.description}
                    </Typography.Paragraph>
                  )}

                  <div css={{ clear: 'both' }} />
                </div>

                <div css={{ marginTop: 12 }}>
                  <Typography.Text strong>
                    {intl.formatMessage({
                      id: 'correction-letter-attachments',
                    })}
                  </Typography.Text>

                  {msg.attachments?.length ? (
                    <div
                      css={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                        marginTop: 8,
                      }}>
                      {msg.attachments.map((a) => {
                        const isPdf = a.name?.toLowerCase()?.includes('.pdf');

                        return (
                          <div
                            key={a.file_guid}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 8,
                            }}>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                minWidth: 0,
                                flex: 1,
                              }}>
                              {isPdf ? (
                                <IconButton
                                  icon={'file-pdf'}
                                  iconStyle={'fad'}
                                  color={'red'}
                                  backgroundOff
                                  customStyle={{ fontSize: '18px' }}
                                  containerStyle={{
                                    maxHeight: '18px',
                                    maxWidth: '18px',
                                    minHeight: '18px',
                                    minWidth: '18px',
                                  }}
                                />
                              ) : (
                                <PaperClipOutlined
                                  rev={''}
                                  style={{ fontSize: '18px' }}
                                />
                              )}

                              <Typography.Text
                                style={{
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  userSelect: 'none',
                                  cursor: 'default',
                                }}
                                title={a.name}>
                                {a.name}
                              </Typography.Text>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <Typography.Text
                      type="secondary"
                      css={{ display: 'block', marginTop: 8 }}>
                      {intl.formatMessage({
                        id: 'correction-letter-attachments-not-implemented',
                      })}
                    </Typography.Text>
                  )}

                  {/* Display Created By and Sent At */}
                  <Typography.Text
                    type="secondary"
                    style={{
                      fontSize: '12px',
                      display: 'block',
                      width: '100%',
                      textAlign: 'right',
                      marginTop: 8,
                    }}>
                    {msg.created_by_full_name} •{' '}
                    {msg.sent_at
                      ? moment.unix(msg.sent_at).format('DD-MM-YYYY, HH:mm')
                      : moment.unix(msg.created_at).format('DD-MM-YYYY, HH:mm')}
                  </Typography.Text>
                </div>
              </div>
            );
          })}
        </div>

        {historyMessages.length > HISTORY_PAGE_SIZE && (
          <div
            css={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: 16,
            }}>
            <Pagination
              current={historyPage}
              total={historyMessages.length}
              pageSize={HISTORY_PAGE_SIZE}
              onChange={setHistoryPage}
              showSizeChanger={false}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const styleFn: ThemedStyleFn = () => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    paddingBottom: 20,
    paddingTop: 2,
  },
});
